import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type BillingCompanyPixKey, priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface PixKeysDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PIX_KEYS_QUERY_KEY = ['billing-company-pix-keys'] as const;

export function PixKeysDialog({ open, onOpenChange }: PixKeysDialogProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const pixKeysQuery = useQuery({
    queryKey: PIX_KEYS_QUERY_KEY,
    queryFn: priceTableApi.findPixKeys,
    enabled: open,
  });

  useEffect(() => {
    if (!pixKeysQuery.data) return;
    setValues(
      Object.fromEntries(pixKeysQuery.data.map((company) => [company.cnpj, company.pixKey ?? ''])),
    );
  }, [pixKeysQuery.data]);

  const saveMutation = useMutation({
    mutationFn: priceTableApi.replacePixKeys,
    onSuccess: (data) => {
      queryClient.setQueryData(PIX_KEYS_QUERY_KEY, data);
      toast({
        variant: 'success',
        title: 'Sucesso',
        description: 'Chaves Pix salvas com sucesso.',
      });
      onOpenChange(false);
    },
  });

  const handleSave = () => {
    if (!pixKeysQuery.data) return;
    saveMutation.mutate(
      pixKeysQuery.data.map((company) => ({
        cnpj: company.cnpj,
        pixKey: values[company.cnpj]?.trim() || null,
      })),
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (saveMutation.isPending) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] max-w-3xl grid-cols-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6">
          <DialogTitle>Cadastro de chave Pix</DialogTitle>
          <DialogDescription>
            Informe a chave Pix usada por cada empresa de faturamento.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-auto px-6">
          {pixKeysQuery.isLoading ? (
            <div className="flex min-h-64 items-center justify-center text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Carregando empresas...
            </div>
          ) : pixKeysQuery.isError ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
              <p className="text-sm text-red-600">Não foi possível carregar as chaves Pix.</p>
              <Button type="button" variant="outline" onClick={() => pixKeysQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : (
            <div className="min-w-[620px]">
              <div className="grid grid-cols-[1fr_1fr] gap-6 border-b px-2 py-3 text-xs font-medium text-gray-500">
                <span>Empresa</span>
                <span>Chave Pix</span>
              </div>
              {pixKeysQuery.data?.map((company: BillingCompanyPixKey) => (
                <div
                  key={company.cnpj}
                  className="grid grid-cols-[1fr_1fr] items-center gap-6 border-b px-2 py-2 last:border-b-0"
                >
                  <span className="text-sm font-medium text-gray-800">{company.companyName}</span>
                  <Input
                    aria-label={`Chave Pix de ${company.companyName}`}
                    placeholder="Informe a chave Pix"
                    value={values[company.cnpj] ?? ''}
                    maxLength={255}
                    disabled={saveMutation.isPending}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [company.cnpj]: event.target.value,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6">
          <FormError error={saveMutation.error} onDismiss={saveMutation.reset} />
        </div>
        <DialogFooter className="shrink-0 border-t bg-white px-6 pb-6 pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={saveMutation.isPending}
            onClick={() => handleOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!pixKeysQuery.data || saveMutation.isPending}
            onClick={handleSave}
          >
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
